import { useMutation } from '@apollo/client';
import {
  questionKeyForQre,
  AnalyticIdentifier,
  isTaxYearTraditional,
  dateStringToReadableMonthYearString,
  centsToDollars,
  dollarsToCents,
} from '@common/neo.tax';
import { InputMasks, ProgressProps } from '@ui-components/neo.tax';
import { useCallback, useMemo } from 'react';

import { CREDIT_CALCULATION_DEBOUNCE_TIMEOUT_MS, CREDIT_CALCULATION_SERIALIZATION_KEY } from '@neo.tax/rd/constants';
import { useRdContext } from '@neo.tax/rd/context';
import { useQuestionSections } from '@neo.tax/rd/hooks';
import {
  Form,
  FormSectionConfiguration,
  FormType,
  getProgress,
  HighlightText,
  makeInputFormConfig,
  validateAmount,
  useAnalyticId,
  FilingMethod,
  QuestionSectionCode,
  QuestionCode,
  useCurrentCompanyOrFail,
  PriorYearQre,
  UpdatePriorYearQreMutation,
  UpdatePriorYearQreMutationVariables,
  UPDATE_PRIOR_YEAR_QRE,
  evictCache,
} from '@neo.tax/shared';

const UPDATE_PRIOR_YEAR_QRES_KEY = 'UPDATE_PRIOR_YEAR_QRES_KEY';
interface PriorYearQresForms {
  priorYearQresForms: FormSectionConfiguration;
  priorYearQresProgress: ProgressProps;
}

export const usePriorYearQresForms = (
  filingMethod?: FilingMethod,
  actualBusinessStartIsoDate?: string | null,
): PriorYearQresForms => {
  const priorYearQresId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_PRIOR_YEAR_QRES);
  const company = useCurrentCompanyOrFail();
  const { fiscalYear } = useRdContext();
  const { priorYearQREs: priorYearQresFromGetCompany } = company;
  const { getAnswer, questionSections } = useQuestionSections();

  const [updatePriorYearQre] = useMutation<UpdatePriorYearQreMutation, UpdatePriorYearQreMutationVariables>(
    UPDATE_PRIOR_YEAR_QRE,
  );

  const firstYearQreAndGrossReceipts = useMemo(() => {
    const firstYearQreAndGrossReceiptsAnswer = getAnswer(
      questionSections,
      QuestionSectionCode.CreditCalculation,
      QuestionCode.FirstYearQreAndPyr,
    );
    return firstYearQreAndGrossReceiptsAnswer ? parseInt(firstYearQreAndGrossReceiptsAnswer) : undefined;
  }, [getAnswer, questionSections]);

  // Fall back to the business start year when the first year hasn't been answered yet. Guard against a
  // malformed date, whose getUTCFullYear() is NaN and would filter out every QRE (qre.year >= NaN).
  const businessStartYear = actualBusinessStartIsoDate ? new Date(actualBusinessStartIsoDate).getUTCFullYear() : NaN;
  const earliestQreYear =
    firstYearQreAndGrossReceipts ?? (Number.isNaN(businessStartYear) ? undefined : businessStartYear);

  // Derive the displayed QREs purely from the company's QREs so changing the first year is
  // reversible.
  const priorYearQres = useMemo<PriorYearQre[]>(() => {
    if (!priorYearQresFromGetCompany) return [];

    let displayedQres = [...priorYearQresFromGetCompany].sort(
      (a, b) => new Date(b.taxYearStartDate).getTime() - new Date(a.taxYearStartDate).getTime(),
    );

    // Exclude the claim year and later — compared by start date to handle fiscal years that span calendar years.
    if (fiscalYear) {
      displayedQres = displayedQres.filter((qre) => qre.taxYearStartDate < fiscalYear.taxYearStartDate);
    }

    if (earliestQreYear !== undefined) {
      displayedQres = displayedQres.filter((qre) => qre.year >= earliestQreYear);
    }

    // ASC only uses the three most recent years.
    if (filingMethod === FilingMethod.Asc) {
      displayedQres = displayedQres.slice(0, 3);
    }

    return displayedQres;
  }, [priorYearQresFromGetCompany, fiscalYear, earliestQreYear, filingMethod]);

  const handlePriorYearQreChange = useCallback(
    async (priorYearQreTaxYearStartDate: string, newValue: string): Promise<void> => {
      if (!newValue || newValue === '') {
        return;
      }
      const newAmount = dollarsToCents(parseFloat(newValue));
      await updatePriorYearQre({
        variables: {
          companyId: company.id,
          taxYearStartDate: priorYearQreTaxYearStartDate,
          amountCents: newAmount,
        },
        update: (cache) => {
          evictCache(cache, ['getCreditEstimate']);
        },
        context: {
          // Keyed per year so a save to one year is not dropped by a save to another within the window.
          debounceKey: `${UPDATE_PRIOR_YEAR_QRES_KEY}:${priorYearQreTaxYearStartDate}`,
          debounceTimeout: CREDIT_CALCULATION_DEBOUNCE_TIMEOUT_MS,
          serializationKey: CREDIT_CALCULATION_SERIALIZATION_KEY,
        },
        optimisticResponse: {
          updatePriorYearQre: {
            __typename: 'Company',
            id: company.id,
            // Map over the full source list, not the filtered display list, so unshown years are preserved.
            priorYearQREs: (priorYearQresFromGetCompany ?? []).map(
              (qre) =>
                ({
                  __typename: 'PriorYearQRE',
                  year: qre.year,
                  amount: qre.taxYearStartDate === priorYearQreTaxYearStartDate ? newAmount : qre.amount,
                  taxYearStartDate: qre.taxYearStartDate,
                  taxYearEndDate: qre.taxYearEndDate,
                }) satisfies PriorYearQre,
            ),
          },
        },
        onError: (error: Error) => {
          console.error('UpdatePriorYearQreMutation in usePriorYearQresForms failed', error);
        },
      });
    },
    [company.id, updatePriorYearQre, priorYearQresFromGetCompany],
  );

  const priorYearQresForms: FormSectionConfiguration = useMemo(
    () => ({
      title: 'Prior Year QREs (Qualified Research Expenses)',
      subtitle: 'Enter Qualified Research Expenses (QREs) for the relevant tax years.',
      forms:
        fiscalYear && priorYearQres && priorYearQres.length > 0
          ? [
              {
                title: 'What were your R&D qualified research expenses (QRE) reported on prior year tax returns?',
                subtitle:
                  'QRE amounts are not estimates. They must have been reported on prior year tax returns on Form 6765. The amounts can be found on line 9 (or line 28) of Form 6765.',
                formType: FormType.FORM_TITLE,
                key: 'qre',
                id: priorYearQresId,
              },
              ...priorYearQres.map<Form>((pyqre) =>
                makeInputFormConfig({
                  id: questionKeyForQre(pyqre.taxYearStartDate),
                  title: (
                    <>
                      What were your QREs in{' '}
                      <HighlightText bold>
                        {isTaxYearTraditional({
                          firstDayOfPeriod: pyqre.taxYearStartDate,
                          lastDayOfPeriod: pyqre.taxYearEndDate,
                        })
                          ? new Date(pyqre.taxYearStartDate).getUTCFullYear()
                          : `Period ${dateStringToReadableMonthYearString(
                              pyqre.taxYearStartDate,
                            )}-${dateStringToReadableMonthYearString(pyqre.taxYearEndDate)}`}
                      </HighlightText>
                      ?
                    </>
                  ),
                  key: questionKeyForQre(pyqre.taxYearStartDate),
                  value: pyqre.amount === undefined || pyqre.amount === null ? '' : centsToDollars(pyqre.amount),
                  type: 'text',
                  min: 0,
                  max: 10000000000,
                  mask: InputMasks.NUMBER,
                  maskReturnValueType: 'unmaskedValue',
                  inlineLeadingContent: '$',
                  validator: validateAmount,
                  onChange: (newValue: string): void => {
                    handlePriorYearQreChange(pyqre.taxYearStartDate, newValue);
                  },
                }),
              ),
            ]
          : [],
    }),
    [fiscalYear, priorYearQres, priorYearQresId, handlePriorYearQreChange],
  );

  const priorYearQresProgress = useMemo(
    () => (priorYearQresForms ? getProgress(priorYearQresForms.forms) : { total: 0, completed: 0 }),
    [priorYearQresForms],
  );

  return { priorYearQresForms, priorYearQresProgress };
};
